/* Four things a steward starts from the front page, each landing on the screen whose primary action
 * it names.
 *
 * **Only actions that exist, and each tile goes where that action is.** "Register a source" is the
 * Connections screen's primary button, "Author a rule" the Rules screen's, "Generate suggestions" the
 * Suggestions screen's, "New contract" the Contracts ledger's. A tile is a door to that control rather
 * than a second copy of it: authoring a rule needs the form its own screen owns, and a dialog rebuilt
 * here would be two answers to "what does creating a rule ask for".
 *
 * **The tile's colour is the navigation section its destination lives in** — Data blue, Quality
 * indigo, AI lavender, Governance mint — so the hue says something true about where the tile goes,
 * and it matches what a reader learns in the rail. It is never the only signal: the icon and the
 * words say the same thing, which is what lets the colours stay pastel.
 *
 * The contract action is role-gated on its own screen, which says so in words when the reader cannot
 * author one. Repeating the gate here would need the reader's grants on the front page for one tile,
 * and the screen that owns the action is the place that already explains it.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { SCREEN_PATHS } from '@/shared/routes';

import { ContractIcon, PlugIcon, ProposeIcon, RuleIcon } from './icons';
import { Panel } from './OverviewParts';

/* Full class strings per section, so every name the page renders is spelled out where a reader can
   find it — never assembled from a suffix. */
const SECTION_CLASS = {
  data: 'lq-ov-action lq-ov-action--data',
  quality: 'lq-ov-action lq-ov-action--quality',
  ai: 'lq-ov-action lq-ov-action--ai',
  governance: 'lq-ov-action lq-ov-action--governance',
} as const;

const ACTIONS = [
  { id: 'source', to: SCREEN_PATHS.connections, section: 'data', Icon: PlugIcon },
  { id: 'rule', to: SCREEN_PATHS.rules, section: 'quality', Icon: RuleIcon },
  { id: 'suggest', to: SCREEN_PATHS.suggestions, section: 'ai', Icon: ProposeIcon },
  {
    id: 'contract',
    to: SCREEN_PATHS.contracts,
    section: 'governance',
    Icon: ContractIcon,
  },
] as const;

export function QuickActions(): ReactElement {
  const { t } = useTranslation();
  return (
    <Panel title={t('home.actions.title')} className="lq-ov-quick">
      <ul className="lq-ov-actions">
        {ACTIONS.map(({ id, to, section, Icon }) => (
          <li key={id} className="lq-ov-actions__slot">
            <Link className={SECTION_CLASS[section]} to={to} data-ov="action">
              <span className="lq-ov-action__icon">
                <Icon className="lq-ov-icon" />
              </span>
              <span className="lq-ov-action__name">{t(`home.actions.${id}.name`)}</span>
              <span className="lq-ov-action__hint">{t(`home.actions.${id}.hint`)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
